import { branchMeta, useMultiBranch } from "@/lib/branchInfo";
import { useEffect, useState } from "react";
// doc-design-wo: standard card-list design
import { PageFrame, DocCard, ItemsPanel, DocMenu, useDocFlow, LinkProjectDialog, timeOf } from "@/components/procurement/DocKit";
import { FileText, GitBranch, FolderInput, Ban, Pencil, Search as SearchIcon, Link2 as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime, fmtMoney, partyLabel } from "@/lib/format";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", rate: "" };


function useSearch() {
  const [q, setQ] = useState("");
  return [q, setQ, (arr, text) => { const t = q.trim().toLowerCase(); return t ? arr.filter((r) => text(r).toLowerCase().includes(t)) : arr; }];
}
function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative w-full max-w-sm">
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10 rounded-lg pl-9" data-testid={testId} />
    </div>
  );
}
const viewBtn = "h-10 rounded-lg px-6 font-semibold";


const isLabour = (i) => /^labou?r\b/i.test(i.description || "");

export default function WorkOrders() {
  const multi = useMultiBranch();
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [wos, setWos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [linking, setLinking] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ, filt] = useSearch();

  const load = async () => {
    setLoading(true);
    try {
      const [woRes, vendorRes, projRes] = await Promise.all([
        api.get("/procurement/work-orders"), api.get("/procurement/vendors"), api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setWos([...woRes.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setVendors(vendorRes.data);
      setProjects(projRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { openFlow, flowNode } = useDocFlow(load);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const project = (id) => projects.find((p) => p.id === id);

  const linkProject = async (wo, projectId) => {
    try {
      await api.patch(`/procurement/work-orders/${wo.id}/project`, { project_id: projectId });
      toast.success(projectId ? "Work order linked — its subcontract cost now counts in that project" : "Work order unlinked from project");
      setLinking(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const act = async (wo, action) => {
    setBusyId(wo.id);
    try {
      await api.post(`/procurement/work-orders/${wo.id}/${action}`, action === "submit" ? undefined : {});
      toast.success(`Work order ${action}ed`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (wo) => {
    if (!await confirmAction(`Delete work order ${wo.number}? This cannot be undone.`)) return;
    try { await api.delete(`/procurement/work-orders/${wo.id}`); toast.success(`${wo.number} deleted`); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const rows = filt(pf.apply(wos, (r) => r.project_id), (w) => `${w.number} ${vendorName(w.vendor_id)} ${project(w.project_id)?.code || ""} ${project(w.project_id)?.name || ""}`);

  return (
    <PageFrame testId="work-orders-page" crumbs={[{ label: "Procurement" }, { label: "Work Orders" }]} title="Work Orders"
      description="Sub-contract work orders — routed through the same numbering & approval engine as a PO."
      actions={<><ProjectFilter filter={pf} />{canWrite && <CreateWODialog companies={companies} vendors={((l) => (l.length ? l : vendors))(vendors.filter((v) => (v.vendor_types || []).includes("WORK")))} projects={projects} onCreated={load} />}</>}>
      <SearchBox value={q} onChange={setQ} placeholder="Search number, vendor or project…" testId="wo-search" />
      {!loading && rows.length === 0 ? (
        <EmptyState title={wos.length ? "No work orders match" : "No work orders yet"} testId="work-orders-empty-state" />
      ) : (
        <div className="space-y-3">
          {rows.map((wo, idx) => {
            const open = expanded[wo.id] ?? (idx === 0 && !q);
            const items = wo.items || [];
            const p = project(wo.project_id);
            return (
              <DocCard key={wo.id} testId={`wo-row-${wo.number}`} toggleId={`wo-toggle-${wo.number}`} open={open} onToggle={() => setExpanded((e) => ({ ...e, [wo.id]: !open }))}
                number={wo.number} date={wo.wo_date} time={timeOf(wo.created_at)} meta={[...branchMeta(wo, multi), `${items.length} line${items.length === 1 ? "" : "s"}`]}
                line={<>
                  <span className="font-medium">{vendorName(wo.vendor_id)}</span>
                  {p ? <span className="text-muted-foreground">{p.code} — {p.name}</span> : <span className="font-medium text-amber-600 dark:text-amber-400">Project not linked</span>}
                </>}
                right={<>
                  <div className="text-right"><p className="text-[11px] text-muted-foreground">Total</p><p className="text-base font-bold tabular-nums">{fmtMoney(wo.total_amount)}</p></div>
                  <StatusPill value={wo.status} />
                  {canWrite && ["DRAFT", "REJECTED"].includes(wo.status) && (
                    <Button size="sm" variant={items.some((i) => !isLabour(i) && Number(i.rate) <= 0) ? "default" : "outline"} className="h-10 rounded-lg" onClick={() => setEditing(wo)} data-testid={`edit-wo-button-${wo.number}`}>
                      <Pencil className="h-3.5 w-3.5" /> {items.some((i) => !isLabour(i) && Number(i.rate) <= 0) ? "Set rates" : "Edit"}
                    </Button>
                  )}
                  {canWrite && wo.status === "DRAFT" && <Button size="sm" variant="outline" className="h-10 rounded-lg" disabled={busyId === wo.id} onClick={() => act(wo, "submit")} data-testid={`submit-wo-button-${wo.number}`}><Send className="h-3.5 w-3.5" /> Submit</Button>}
                  {wo.status === "SUBMITTED" && <>
                    <Button size="sm" className="h-10 rounded-lg" disabled={busyId === wo.id} onClick={() => act(wo, "approve")} data-testid={`approve-wo-button-${wo.number}`}><Check className="h-3.5 w-3.5" /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-10 rounded-lg" disabled={busyId === wo.id} onClick={() => act(wo, "reject")} data-testid={`reject-wo-button-${wo.number}`}><X className="h-3.5 w-3.5 text-rose-500" /> Reject</Button>
                  </>}
                  <ApprovalReviewButton docType="WO" docId={wo.id} onChanged={load} icon={FileText} className={viewBtn} />
                  <DocMenu testId={`wo-menu-${wo.number}`} items={[
                    canWrite && wo.status !== "CANCELLED" && { key: "proj", label: "Change project", icon: FolderInput, onSelect: () => setLinking(wo), testId: `wo-project-${wo.number}` },
                    { key: "flow", label: wo.status === "APPROVED" ? "Document flow / Cancel" : "Document flow", icon: GitBranch, onSelect: () => openFlow("WORK_ORDER", wo.id, wo.status === "APPROVED" ? `/procurement/work-orders/${wo.id}/cancel` : undefined) },
                    canWrite && ["DRAFT", "REJECTED", "CANCELLED"].includes(wo.status) && { key: "del", label: "Delete", icon: Trash2, danger: true, onSelect: () => remove(wo), testId: `wo-delete-${wo.number}` },
                  ]} />
                </>}>
                {(wo.site_location || wo.terms) && (
                  <div className="mb-3 space-y-1 text-sm text-muted-foreground">
                    {wo.site_location && <p>Site: <span className="text-foreground">{wo.site_location}</span></p>}
                    {wo.terms && <p className="text-foreground">{wo.terms}</p>}
                  </div>
                )}
                <ItemsPanel testId={`wo-items-${wo.number}`}
                  columns={[{ label: "Description", w: "3fr" }, { label: "Qty", w: "1fr", right: true }, { label: "Rate", w: "1fr", right: true }, { label: "Amount", w: "1.4fr", right: true }]}
                  rows={items.map((i) => isLabour(i) && Number(i.amount) === 0
                    ? [i.description, <span className="text-muted-foreground">—</span>, `₹${fmtMoney(i.rate)}`, <span className="text-xs text-muted-foreground">Billed by attendance</span>]
                    : [i.description, Number(i.quantity), `₹${fmtMoney(i.rate)}`, `₹${fmtMoney(i.amount)}`])} />
              </DocCard>
            );
          })}
        </div>
      )}
      <LinkProjectDialog doc={linking} projects={projects} onClose={() => setLinking(null)} onSave={linkProject} />
      <EditWODialog wo={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      {flowNode}
    </PageFrame>
  );
}

function CreateWODialog({ companies, vendors, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", vendor_id: "", wo_date: "", site_location: "", tax_pct: "0" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [sourceSr, setSourceSr] = useState("");
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.rate,
  } : it)));

  // Approved service requisitions that haven't become a Work Order yet
  useEffect(() => {
    if (!open) return;
    api.get("/procurement/service-requisitions")
      .then(({ data }) => setRequisitions((data || []).filter((r) => r.status === "APPROVED" && !r.work_order_id)))
      .catch(() => setRequisitions([]));
  }, [open]);

  const copyFrom = (srId) => {
    setSourceSr(srId === "none" ? "" : srId);
    const sr = requisitions.find((r) => r.id === srId);
    if (!sr) return;
    setForm((f) => ({ ...f, company_id: sr.company_id || f.company_id, project_id: sr.project_id || f.project_id }));
    setItems(sr.items.map((i) => ({ ...EMPTY_ITEM, description: i.description, quantity: String(Number(i.quantity)), rate: "", wbs_item_id: i.wbs_item_id || "" })));
    toast.info("Scope copied — now choose the contractor and enter a rate for each line");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) {
      toast.error("Pick a vendor first");
      return;
    }
    setSaving(true);
    try {
      const { data: wo } = await api.post("/procurement/work-orders", { ...form, project_id: form.project_id || null, items: items.map((i) => ({ ...i, wbs_item_id: i.wbs_item_id || null })) });
      if (sourceSr) {
        try {
          await api.post(`/procurement/service-requisitions/${sourceSr}/link-work-order`, { work_order_id: wo.id });
        } catch (linkErr) {
          toast.warning(`Work order created, but the requisition could not be marked converted: ${getErrorMessage(linkErr)}`);
        }
      }
      setSourceSr("");
      toast.success(sourceSr ? `Work order ${wo.number} created from the requisition` : "Work order created");
      setForm({ company_id: "", project_id: "", vendor_id: "", wo_date: "", site_location: "", tax_pct: "0" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-wo-button" data-tour="wo-new"><Plus className="h-4 w-4" /> New Work Order</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-wo-form">
          <div className="rounded-lg border border-dashed border-border p-3">
            <Label>Copy from service requisition <span className="font-normal text-muted-foreground">(fills company, project and work lines)</span></Label>
            <Select value={sourceSr || "none"} onValueChange={copyFrom}>
              <SelectTrigger className="mt-1.5 [&>span]:truncate" data-testid="wo-source-sr-select"><SelectValue placeholder="Start blank" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Start blank</SelectItem>
                {requisitions.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.scope_of_work || `${r.items.length} line(s)`}</SelectItem>)}
              </SelectContent>
            </Select>
            {requisitions.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">No approved requisitions waiting. Submit and approve one on the Service Requisitions page.</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="wo-company-select" />
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="wo-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="wo-date-input" value={form.wo_date} onChange={set("wo_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(its subcontract cost is booked there)</span></Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="wo-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {!form.project_id && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Without a project, this work order's cost won't appear in any project's finance.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Site location</Label><Input data-testid="wo-site-input" value={form.site_location} onChange={set("site_location")} className="mt-1.5" /></div>
            <div><Label>Tax %</Label><Input type="number" step="0.01" data-testid="wo-tax-input" value={form.tax_pct} onChange={set("tax_pct")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`wo-item-row-${idx}`}>
                  <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} prefer="SERVICE" data-testid={`wo-item-desc-input-${idx}`} />
                  <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`wo-item-qty-input-${idx}`} />
                  <Input required type="number" step="0.01" placeholder="Rate" className="w-24" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} data-testid={`wo-item-rate-input-${idx}`} />
                  {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-wo-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-wo-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/** Price a draft work order: the one that approving a service requisition drafts has its rates at 0. */
function EditWODialog({ wo, onClose, onSaved }) {
  const [lines, setLines] = useState([]);
  const [taxPct, setTaxPct] = useState("0");
  const [terms, setTerms] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wo) return;
    setLines((wo.items || []).map((i) => ({ id: i.id, description: i.description, quantity: String(i.quantity), rate: String(i.rate), discount_pct: String(i.discount_pct || 0), labour: i.description?.startsWith("Labour — ") })));
    setTaxPct(Number(wo.subtotal) > 0 ? String(Math.round((Number(wo.tax_amount) / Number(wo.subtotal)) * 10000) / 100) : "0");
    setTerms(wo.terms || "");
  }, [wo]);

  const setLine = (id, field, value) => setLines((all) => all.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  const amount = (l) => Number(l.quantity || 0) * Number(l.rate || 0) * (1 - Number(l.discount_pct || 0) / 100);
  const subtotal = lines.reduce((sum, l) => sum + amount(l), 0);
  const tax = (subtotal * Number(taxPct || 0)) / 100;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/procurement/work-orders/${wo.id}`, {
        tax_pct: taxPct || "0", terms: terms || null,
        items: lines.map((l) => ({ id: l.id, quantity: l.quantity, rate: l.rate, discount_pct: l.discount_pct || "0", description: l.description })),
      });
      toast.success("Work order updated — you can submit it now");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!wo} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{wo ? `Edit ${wo.number}` : "Edit work order"}</DialogTitle></DialogHeader>
        {wo && (
          <div className="space-y-3" data-testid="edit-wo-form">
            <div className="space-y-2">
              {lines.map((l) => (
                <div key={l.id} className="grid grid-cols-[2fr_1fr_1fr_1fr] items-end gap-2 rounded-md border border-border p-2">
                  <div className="col-span-4 text-sm font-medium text-foreground">{l.description}</div>
                  <div><Label className="text-xs">Qty</Label><Input type="number" step="0.001" min="0" value={l.quantity} onChange={(e) => setLine(l.id, "quantity", e.target.value)} disabled={l.labour} className="mt-1" /></div>
                  <div><Label className="text-xs">Rate (₹)</Label><Input type="number" step="0.01" min="0" value={l.rate} onChange={(e) => setLine(l.id, "rate", e.target.value)} className="mt-1" data-testid={`edit-wo-rate-${l.id}`} /></div>
                  <div><Label className="text-xs">Discount %</Label><Input type="number" step="0.01" min="0" max="100" value={l.discount_pct} onChange={(e) => setLine(l.id, "discount_pct", e.target.value)} disabled={l.labour} className="mt-1" /></div>
                  <div className="text-right font-mono text-sm">{fmtMoney(amount(l))}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>GST %</Label><Input type="number" step="0.01" min="0" max="100" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="mt-1.5" data-testid="edit-wo-tax" /></div>
              <div className="rounded-lg bg-muted/50 p-2.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{fmtMoney(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="font-mono">{fmtMoney(tax)}</span></div>
                <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono">{fmtMoney(subtotal + tax)}</span></div>
              </div>
            </div>
            <div><Label>Terms</Label><Input value={terms} onChange={(e) => setTerms(e.target.value)} className="mt-1.5" /></div>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="edit-wo-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
